/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, Fragment, useEffect, useRef } from "react"
import { notification } from "@duxton-web/ui"
import { OrdersOutlined } from "@duxton-web/icons"
import { useHistory } from "react-router-dom"
import { ORDER_MANAGER_URL_PREFIX } from "constants/routes"
import { isInOrderListPage } from "order-manager/utils/order_helper"
import { HermesStore, HermesConnectionStatus } from "connections/hermes"
import { OrderNotification } from "order-manager/types/models/notification"
import { getGrabXValue, GrabXDataTypes, MerchantPortalFeatureFlag } from "grabx"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { OrderPageType } from "order-manager/constants/orderState"
import {
  TabCardViewKeys,
  TabTableViewKeys,
} from "order-manager/constants/tabview"
import { autoAcceptGroupMap } from "order-manager/constants/autoAcceptGroup"
import { getFoodMerchantUrl } from "constants/domain"
import { ViewMode } from "order-manager/constants/viewMode"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import {
  setupBeforeUnloadListener,
  setupNotificationConfig,
  monitorActiveTab,
  monitorHermesConnection,
  updateOpenedTabsRegularly,
} from "order-manager/utils/notification_helper"
import { CURRENT_TAB_KEY } from "constants/sessions_storage"
import { getOrderManagerNotificationConfig } from "order-manager/utils/storage_config_helper"
import { TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventCategory,
  ShowOn,
} from "order-manager/constants/tracking"
import { storageHelper } from "utils/storage"

interface OrderNotificationComponentProps {}

export const OrderNotificationComponent: FC<OrderNotificationComponentProps> = () => {
  // the latter part is for tests to run successfully
  const notificationAvailable = "Notification" in window

  if (
    notificationAvailable &&
    typeof Notification?.requestPermission === "function" &&
    Notification.permission !== "granted"
  ) {
    Notification.requestPermission()
  }

  const dispatch = useDispatch()
  // Instantiate here so object properties can be shared between method
  const hermesObject = new HermesStore()

  const {
    orderManagerMerchant: { maxAPIStore },
    orderManager: {
      newOrderNotification,
      orderNotificationCount,
      orderManagerConfig,
    },
  } = useSelector((state: AppState) => state)

  const history = useHistory()
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const textMap = useTranslationsMapTyped({
    header: "ordermanager.notification.header",
    body: "ordermanager.notification.body",
  })

  const addCounterToFavicon = (count: Number) => {
    const favicon = document.getElementById("favicon") as HTMLLinkElement
    const faviconSize = 64

    // Remove notification badge from favicon if count = 0
    if (count === 0) {
      favicon?.setAttribute("href", "/images/grabfavicon.ico")
      return
    }

    const canvas = document.createElement("canvas")
    canvas.width = faviconSize
    canvas.height = faviconSize

    const context = canvas.getContext("2d")
    const img = document.createElement("img")
    img.src = favicon.href

    img.onload = () => {
      if (context !== null) {
        // Draw Original Favicon as Background
        context.drawImage(img, 0, 0, faviconSize, faviconSize)

        // Draw Notification Circle
        context.beginPath()
        context.arc(
          canvas.width - faviconSize / 3,
          faviconSize / 3,
          faviconSize / 3,
          0,
          2 * Math.PI,
        )
        context.fillStyle = "#FF0000"
        context.fill()

        // Draw Notification Number
        context.font = '35px "helvetica", sans-serif'
        context.textAlign = "center"
        context.textBaseline = "middle"
        context.fillStyle = "#FFFFFF"
        context.fillText(
          count.toString(),
          canvas.width - faviconSize / 3,
          faviconSize / 3,
        )
      }

      // Replace favicon
      favicon?.setAttribute("href", canvas.toDataURL("image/png"))
    }
  }

  const clickNotification = (
    newOrder: OrderNotification,
    fromHiddenState: boolean,
  ) => {
    window.focus()

    const selectedTab =
      orderManagerConfig?.defaultView === ViewMode.Card
        ? TabCardViewKeys.ongoing
        : TabTableViewKeys.preparing

    if (!isInOrderListPage() || fromHiddenState) {
      history.push(
        `${ORDER_MANAGER_URL_PREFIX}/${newOrder?.merchantID}/${selectedTab}/${newOrder?.orderID}`,
      )
      dispatch(actionCreator.orderManager.changeOrderNotificationCount(0))
    }
  }

  const displayNotification = (newOrder: OrderNotification) => {
    let showOn: string = ShowOn.Unknown

    if (document.visibilityState === "visible") {
      notification.open({
        placement: "topRight",
        duration: 5,
        icon: <OrdersOutlined style={{ color: "#00b14f", fontSize: 24 }} />,
        message: textMap.header.replace(
          /<short.order.id>/i,
          newOrder?.shortOrderID || "",
        ),
        description: textMap.body,
        style: {
          cursor: isInOrderListPage() ? "text" : "pointer",
        },
        onClick: () => {
          clickNotification(newOrder, false)
        },
      })

      showOn = ShowOn.InsidePageApp
    }

    if (document.visibilityState === "hidden" && notificationAvailable) {
      const notif = new Notification(
        textMap.header.replace(
          /<short.order.id>/i,
          newOrder?.shortOrderID || "",
        ),
        {
          body: textMap.body,
          icon: "/images/grabfavicon.ico",
        },
      )

      notif.onclick = () => {
        clickNotification(newOrder, true)
      }

      showOn = ShowOn.OutsidePageApp
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.notification,
      action: EventAction.displayMedia,
      labels: {
        SHOW_ON: showOn,
        NOTIFICATION_SOURCE: newOrder.source,
        ...(newOrder.isRetryFromMonitoring && {
          SWITCHED_BACK_TO_HERMES: newOrder.isRetryFromMonitoring,
        }),
      },
    })
  }

  useEffect(() => {
    const initNotificationEngine = async () => {
      setupNotificationConfig()
      const cleanupBeforeUnload = setupBeforeUnloadListener()
      monitorActiveTab(hermesObject)
      updateOpenedTabsRegularly()
      const notifConfig = getOrderManagerNotificationConfig()

      const enableHermesConnection = await getGrabXValue<boolean>(
        MerchantPortalFeatureFlag.portalEnableHermesConnection,
        GrabXDataTypes.Boolean,
        false,
      )

      if (!enableHermesConnection) {
        notifConfig.enableFallback = true
        notifConfig.enableHermes = false
        storageHelper.setOrderManagerNotificationConfig(notifConfig)
        return cleanupBeforeUnload
      }

      monitorHermesConnection(hermesObject)
      /*
          Only trigger hermes connection if
          - enable falback is false AND
          - notification is not owned by any tab OR this tab is current active tab
      */
      if (
        !notifConfig.enableFallback &&
        (sessionStorage.getItem(CURRENT_TAB_KEY) === "" ||
          sessionStorage.getItem(CURRENT_TAB_KEY) === notifConfig.activeTabID)
      ) {
        if (
          hermesObject.connectionStatus === HermesConnectionStatus.Disconnected
        ) {
          hermesObject.connect(false)
        }
      }

      return cleanupBeforeUnload
    }

    let cleanup: (() => void) | undefined

    initNotificationEngine().then(cleanupFn => {
      cleanup = cleanupFn
    })

    return () => {
      cleanup?.()
    }
  }, [])

  useEffect(() => {
    if (!newOrderNotification?.merchantID) {
      return
    }

    if (notificationAvailable && Notification.permission === "granted") {
      displayNotification(newOrderNotification)

      // Lazy-load audio: create only when needed, then reuse
      if (!audioRef.current) {
        audioRef.current = new Audio(
          `${getFoodMerchantUrl()}/sounds/order-new.mp3`,
        )
      }
      audioRef.current.play().catch(error => {
        TroyWebTracker.sendEvent({
          category: EventCategory.notification,
          action: EventAction.errorPlayingSound,
          labels: {
            ERROR: error,
          },
        })
      })
    }

    setTimeout(() => {
      dispatch(actionCreator.orderManager.changeOrderNotificationCount(0))
    }, 10000)

    // Prevent reload if merchant is not in order page or skipRefreshOrderList is true
    if (!isInOrderListPage() || newOrderNotification?.skipRefreshOrderList) {
      return
    }

    dispatch(
      actionCreator.orderManager.fetchOrderList({
        merchantID: newOrderNotification?.merchantID || "",
        pageType: OrderPageType.PREPARINGV2,
        autoAcceptGroup:
          autoAcceptGroupMap.get(maxAPIStore?.merchant.autoAcceptGroup || "") ||
          0,
        searchToken: "",
      }),
    )

    dispatch(
      actionCreator.orderManager.fetchOrderList({
        merchantID: newOrderNotification?.merchantID || "",
        pageType: OrderPageType.READY,
        autoAcceptGroup:
          autoAcceptGroupMap.get(maxAPIStore?.merchant.autoAcceptGroup || "") ||
          0,
        searchToken: "",
      }),
    )

    dispatch(
      actionCreator.orderManager.fetchOrderList({
        merchantID: newOrderNotification?.merchantID || "",
        pageType: OrderPageType.UPCOMING,
        autoAcceptGroup:
          autoAcceptGroupMap.get(maxAPIStore?.merchant.autoAcceptGroup || "") ||
          0,
        searchToken: "",
      }),
    )
  }, [newOrderNotification])

  useEffect(() => {
    addCounterToFavicon(orderNotificationCount)
  }, [orderNotificationCount])

  return <Fragment />
}
